/**
 * ArtistAvailabilityEditor — shared availability editing UI
 *
 * Used by ManageAvailability (admin, all artists) and ArtistPortal (self-service,
 * own artist only). Both pages point at different API paths but share identical
 * components, so changes in one place are reflected everywhere automatically.
 */
import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { queryClient } from "@/lib/queryClient";
import {
  ChevronLeft, ChevronRight, X, RefreshCw, CalendarDays, CalendarOff,
  Save, Check, AlertCircle, RotateCw, CheckCircle2, Clock, XCircle,
} from "lucide-react";
import { captureException } from "@/lib/sentry";

// ── Shared constants ────────────────────────────────────────────────────────

export const STATUS_OPTIONS = [
  {
    value: "available",
    label: "Available",
    icon: CheckCircle2,
    color: "#22c55e",
    bg: "#22c55e18",
    border: "#22c55e40",
    description: "Clients can view this artist and submit booking requests through the booking form.",
  },
  {
    value: "limited",
    label: "Limited",
    icon: Clock,
    color: "#f59e0b",
    bg: "#f59e0b18",
    border: "#f59e0b40",
    description: "Booking form shows limited availability — use when accepting select projects only.",
  },
  {
    value: "booked",
    label: "Booked Out",
    icon: XCircle,
    color: "#ef4444",
    bg: "#ef444418",
    border: "#ef444440",
    description: "Artist appears greyed out on the booking form and cannot be selected — clients can join the waitlist instead.",
  },
] as const;

export function getStatusMeta(s: string) {
  return STATUS_OPTIONS.find((o) => o.value === s) ?? STATUS_OPTIONS[0];
}

// ── Types ───────────────────────────────────────────────────────────────────

export interface AvailabilityData {
  status: string;
  nextAvailableDate: string | null;
  minWeeksOut: number;
  note: string;
  blockedDates: string[];
  recurringBlockedDays: number[];
}

export interface AvailabilityEditorProps {
  /** Used only for `data-testid` scoping — not displayed. */
  artistSlug: string;
  data: AvailabilityData;
  /** PUT endpoint that accepts { status, nextAvailableDate, minWeeksOut, note } */
  savePutUrl: string;
  /** POST / DELETE endpoint that accepts { date } */
  blockedDatesUrl: string;
  /** PUT endpoint that accepts { days: number[] } */
  recurringDaysUrl: string;
  /** React Query cache key to invalidate/update on mutations. */
  queryKey: unknown[];
  /** Optional accent color override (uses status color by default). */
  accentColor?: string;
}

// ── BlockedDaysCalendar ─────────────────────────────────────────────────────

export function BlockedDaysCalendar({
  artistSlug,
  blockedDates,
  recurringBlockedDays,
  accentColor,
  blockedDatesUrl,
  recurringDaysUrl,
  queryKey,
  onBlockedDatesChange,
  onRecurringDaysChange,
}: {
  artistSlug: string;
  blockedDates: string[];
  recurringBlockedDays: number[];
  accentColor: string;
  blockedDatesUrl: string;
  recurringDaysUrl: string;
  queryKey: unknown[];
  /** Optimistic update callback — receives the full new list after toggle */
  onBlockedDatesChange?: (dates: string[]) => void;
  onRecurringDaysChange?: (days: number[]) => void;
}) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const blockedSet = useMemo(() => new Set(blockedDates), [blockedDates]);
  const recurringSet = useMemo(() => new Set(recurringBlockedDays), [recurringBlockedDays]);

  const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const monthName = new Date(currentYear, currentMonth).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const isPast = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date < todayStart;
  };

  const isPastMonth =
    currentYear < today.getFullYear() ||
    (currentYear === today.getFullYear() && currentMonth < today.getMonth());

  const dateStr = (day: number) =>
    `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const toggleMutation = useMutation({
    mutationFn: async ({ date, isBlocked }: { date: string; isBlocked: boolean }) => {
      const res = await fetch(blockedDatesUrl, {
        method: isBlocked ? "DELETE" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        captureException(new Error((j as { error?: string }).error || "Failed to update"));
        throw new Error((j as { error?: string }).error || "Failed to update");
      }
      return res.json();
    },
    onSuccess: (_data, { date, isBlocked }) => {
      const next = isBlocked
        ? blockedDates.filter((d) => d !== date)
        : [...blockedDates, date];
      onBlockedDatesChange?.(next);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const recurringMutation = useMutation({
    mutationFn: async (days: number[]) => {
      const res = await fetch(recurringDaysUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        captureException(new Error((j as { error?: string }).error || "Failed to update"));
        throw new Error((j as { error?: string }).error || "Failed to update");
      }
      return res.json();
    },
    onSuccess: (data) => {
      onRecurringDaysChange?.(data.recurringBlockedDays ?? []);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const toggleRecurringDay = (i: number) => {
    const next = recurringSet.has(i)
      ? Array.from(recurringSet).filter((d) => d !== i)
      : [...Array.from(recurringSet), i];
    recurringMutation.mutate(next);
  };

  const toggleDate = (day: number) => {
    if (isPast(day)) return;
    const ds = dateStr(day);
    toggleMutation.mutate({ date: ds, isBlocked: blockedSet.has(ds) });
  };

  const blockedThisMonth = Array.from({ length: daysInMonth }, (_, i) => {
    const ds = dateStr(i + 1);
    return blockedSet.has(ds) ? ds : null;
  }).filter(Boolean) as string[];

  return (
    <div
      style={{ padding: "18px 18px 16px", background: "#0d0d18" }}
      data-testid={`blocked-calendar-${artistSlug}`}
    >
      {/* Month navigation */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <button
          onClick={prevMonth}
          disabled={isPastMonth}
          data-testid="button-prev-month"
          style={{
            width: 36, height: 36, borderRadius: 8,
            background: isPastMonth ? "transparent" : "#ffffff10",
            border: "1px solid #ffffff15",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: isPastMonth ? "not-allowed" : "pointer",
            opacity: isPastMonth ? 0.25 : 1,
          }}
        >
          <ChevronLeft style={{ width: 16, height: 16, color: "#aaa" }} />
        </button>

        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>{monthName}</div>
          {blockedThisMonth.length > 0 && (
            <div style={{ color: "#ef4444", fontSize: 11, marginTop: 2 }}>
              {blockedThisMonth.length} day{blockedThisMonth.length !== 1 ? "s" : ""} blocked
            </div>
          )}
        </div>

        <button
          onClick={nextMonth}
          data-testid="button-next-month"
          style={{
            width: 36, height: 36, borderRadius: 8, background: "#ffffff10",
            border: "1px solid #ffffff15", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer",
          }}
        >
          <ChevronRight style={{ width: 16, height: 16, color: "#aaa" }} />
        </button>
      </div>

      <p style={{ color: "#999", fontSize: 12, textAlign: "center", marginBottom: 10, lineHeight: 1.5 }}>
        Tap a <strong style={{ color: "#ccc", fontWeight: 700 }}>day name</strong> to block every occurrence.
        Tap a <strong style={{ color: "#ccc", fontWeight: 700 }}>date</strong> to block/unblock individually.
      </p>

      {/* Day headers — tap to toggle recurring */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {DAYS.map((d, i) => {
          const isRecurring = recurringSet.has(i);
          return (
            <button
              key={d}
              onClick={() => toggleRecurringDay(i)}
              data-testid={`day-header-${d}`}
              title={isRecurring ? `Unblock every ${d}` : `Block every ${d}`}
              style={{
                textAlign: "center", fontSize: 12, fontWeight: 700,
                padding: "6px 2px", borderRadius: 6,
                border: isRecurring ? "1px solid #ef444450" : "1px solid transparent",
                background: isRecurring ? "#ef444420" : "#ffffff06",
                color: isRecurring ? "#ef4444" : "#bbb",
                cursor: "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                transition: "all 0.12s",
              }}
            >
              {d}
              {isRecurring && <RotateCw style={{ width: 8, height: 8 }} />}
            </button>
          );
        })}
      </div>

      {/* Day grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const ds = dateStr(day);
          const isBlocked = blockedSet.has(ds);
          const past = isPast(day);
          const isToggling = toggleMutation.isPending && toggleMutation.variables?.date === ds;
          const weekday = new Date(currentYear, currentMonth, day).getDay();
          const isRecurringDay = recurringSet.has(weekday);

          let bg = "#ffffff06";
          let border = "1px solid transparent";
          let color = "#bbb";
          let fontWeight: number = 400;

          if (past) {
            bg = "transparent"; color = "#4a4a4a"; border = "1px solid transparent";
          } else if (isToggling) {
            bg = `${accentColor}25`; color = accentColor; border = `1px solid ${accentColor}40`;
          } else if (isBlocked || isRecurringDay) {
            bg = "#ef444420"; border = "1px solid #ef444450"; color = "#ef4444"; fontWeight = 700;
          }

          return (
            <button
              key={day}
              onClick={() => toggleDate(day)}
              disabled={past || isToggling}
              data-testid={`block-day-${day}`}
              style={{
                aspectRatio: "1", minHeight: 40, borderRadius: 8,
                border, background: bg, color, fontWeight, fontSize: 13,
                cursor: past ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative", transition: "all 0.12s",
              }}
            >
              {day}
              {isBlocked && !isToggling && (
                <X style={{ width: 8, height: 8, position: "absolute", top: 4, right: 4, color: "#ef4444" }} />
              )}
              {!isBlocked && isRecurringDay && !past && !isToggling && (
                <RotateCw style={{ width: 7, height: 7, position: "absolute", top: 4, right: 4, color: "#ef444460" }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Recurring days summary */}
      {recurringSet.size > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={{ color: "#999", fontSize: 11, fontWeight: 600 }}>Every:</span>
          {Array.from(recurringSet).sort().map((d) => (
            <span key={d} style={{ background: "#ef444415", color: "#ef4444", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 6, border: "1px solid #ef444425" }}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]}
            </span>
          ))}
        </div>
      )}

      {/* Individual blocked dates this month */}
      {blockedThisMonth.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {blockedThisMonth.map((d) => (
            <span key={d} style={{ background: "#ef444415", color: "#ef4444", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 6, border: "1px solid #ef444425" }}>
              {new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ArtistAvailabilityEditor ────────────────────────────────────────────────

/**
 * Full availability editing form for a single artist.
 * Used in ManageAvailability (admin view) and ArtistPortal (self-service).
 * Both use the same component — only the API base paths differ.
 */
export function ArtistAvailabilityEditor({
  artistSlug,
  data,
  savePutUrl,
  blockedDatesUrl,
  recurringDaysUrl,
  queryKey,
  accentColor,
}: AvailabilityEditorProps) {
  const [status, setStatus] = useState(data.status);
  const [nextDate, setNextDate] = useState(data.nextAvailableDate || "");
  const [minWeeks, setMinWeeks] = useState(data.minWeeksOut);
  const [note, setNote] = useState(data.note || "");
  const [saved, setSaved] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showBookedConfirm, setShowBookedConfirm] = useState(false);

  // Local optimistic state for blocked dates / recurring days
  const [localBlockedDates, setLocalBlockedDates] = useState(data.blockedDates);
  const [localRecurringDays, setLocalRecurringDays] = useState(data.recurringBlockedDays);

  // Keep local state in sync when the parent data re-fetches
  useMemo(() => { setLocalBlockedDates(data.blockedDates); }, [data.blockedDates]);
  useMemo(() => { setLocalRecurringDays(data.recurringBlockedDays); }, [data.recurringBlockedDays]);

  const meta = getStatusMeta(status);
  const effectiveAccent = accentColor ?? meta.color;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(savePutUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status,
          nextAvailableDate: nextDate || null,
          minWeeksOut: minWeeks,
          note: note || null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        captureException(new Error((j as { error?: string }).error || "Failed to save"));
        throw new Error((j as { error?: string }).error || "Failed to save");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  // Effective first-bookable date banner
  const effectiveDateBanner = (() => {
    const sliderDate = new Date();
    sliderDate.setDate(sliderDate.getDate() + minWeeks * 7);
    const pickerDate = nextDate ? new Date(nextDate + "T00:00:00") : null;
    const pickerWins = pickerDate && pickerDate > sliderDate;
    const effectiveDate = pickerWins ? pickerDate! : sliderDate;
    const effectiveStr = effectiveDate.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
    const driver = pickerWins ? "Next Available Date" : `${minWeeks}-week advance notice`;
    return { effectiveStr, driver };
  })();

  return (
    <div>
      {/* Status picker */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: "block", color: "#666", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Status
        </label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }} data-testid={`status-group-${artistSlug}`}>
          {STATUS_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = status === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setStatus(opt.value)}
                data-testid={`status-btn-${opt.value}-${artistSlug}`}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 16px", borderRadius: 8,
                  border: `1px solid ${active ? opt.border : "#ffffff15"}`,
                  background: active ? opt.bg : "#ffffff06",
                  color: active ? opt.color : "#666",
                  fontWeight: active ? 700 : 500, fontSize: 13,
                  cursor: "pointer", transition: "all 0.15s", minHeight: 40,
                }}
              >
                <Icon style={{ width: 15, height: 15 }} />
                {opt.label}
              </button>
            );
          })}
        </div>
        <div style={{ background: `${meta.color}0e`, border: `1px solid ${meta.color}28`, borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
          {(() => { const Icon = meta.icon; return <Icon style={{ width: 13, height: 13, color: meta.color, flexShrink: 0 }} />; })()}
          <span style={{ fontSize: 12, color: "#aaa", lineHeight: 1.4 }}>{meta.description}</span>
        </div>
      </div>

      {/* Next Available Date */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <label style={{ color: "#666", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Next Available Date
          </label>
          <span style={{ background: "#ffffff08", color: "#888", fontSize: 11, padding: "2px 8px", borderRadius: 10, border: "1px solid #ffffff10", whiteSpace: "nowrap" }}>
            optional hard cutoff
          </span>
        </div>
        <input
          type="date"
          value={nextDate}
          onChange={(e) => setNextDate(e.target.value)}
          data-testid={`input-date-${artistSlug}`}
          style={{ width: "100%", background: "#0d0d18", border: "1px solid #ffffff15", borderRadius: 8, padding: "9px 12px", color: nextDate ? "#fff" : "#444", fontSize: 13, outline: "none", boxSizing: "border-box" }}
        />
      </div>

      {/* Min Advance Notice */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <label style={{ color: "#666", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Min. Advance Notice
          </label>
          <span style={{ background: "#ffffff08", color: "#888", fontSize: 11, padding: "2px 8px", borderRadius: 10, border: "1px solid #ffffff10", whiteSpace: "nowrap" }}>
            rolling window from today
          </span>
        </div>
        <input
          type="range" min={1} max={12} value={minWeeks}
          onChange={(e) => setMinWeeks(parseInt(e.target.value))}
          data-testid={`input-weeks-${artistSlug}`}
          style={{ width: "100%", accentColor: effectiveAccent, marginBottom: 8, display: "block" }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ background: "#0d0d18", border: "1px solid #ffffff15", borderRadius: 8, padding: "5px 12px" }}>
            <span style={{ color: effectiveAccent, fontWeight: 700, fontSize: 14 }}>{minWeeks}</span>
            <span style={{ color: "#888", fontSize: 11 }}> wk{minWeeks !== 1 ? "s" : ""}</span>
          </div>
          <span style={{ color: "#666", fontSize: 12 }}>
            {minWeeks === 1 ? "1 week from today" : `${minWeeks} weeks from today`}
          </span>
        </div>
      </div>

      {/* Effective date banner */}
      <div style={{ marginBottom: 18, background: `${meta.color}12`, border: `1px solid ${meta.color}30`, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <CalendarDays style={{ width: 16, height: 16, color: meta.color, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13, color: "#ddd" }}>
            Clients can first book on{" "}
            <strong style={{ color: "#fff", fontWeight: 700 }}>{effectiveDateBanner.effectiveStr}</strong>
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            Controlled by: <span style={{ color: meta.color, fontWeight: 600 }}>{effectiveDateBanner.driver}</span>
          </div>
        </div>
      </div>

      {/* Public note */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: "block", color: "#666", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
          Public Note <span style={{ color: "#444", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(shown to clients)</span>
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Booking ~3 months out, contact shop to inquire"
          data-testid={`input-note-${artistSlug}`}
          style={{ width: "100%", background: "#0d0d18", border: "1px solid #ffffff15", borderRadius: 8, padding: "9px 12px", color: "#e0e0e0", fontSize: 13, outline: "none", boxSizing: "border-box" }}
        />
      </div>

      {/* Booked-Out Confirmation Modal */}
      {showBookedConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#111", border: "1px solid #ef444440", borderRadius: 16, padding: "28px 32px", maxWidth: 420, width: "100%", boxShadow: "0 24px 80px rgba(0,0,0,0.8)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#ef444418", border: "1px solid #ef444440", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AlertCircle style={{ width: 20, height: 20, color: "#ef4444" }} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>Set Artist to Booked Out?</div>
            </div>
            <p style={{ fontSize: 13, color: "#bbb", lineHeight: 1.7, margin: "0 0 10px" }}>
              Customers will <strong style={{ color: "#fff" }}>not</strong> be able to select this artist or submit a booking request. They'll see the artist as unavailable and will be offered the <strong style={{ color: "#ef4444" }}>waitlist</strong> instead.
            </p>
            <p style={{ fontSize: 12, color: "#666", lineHeight: 1.6, margin: "0 0 24px" }}>
              Any existing confirmed bookings are not affected. You can reverse this at any time by switching the status back to Available or Limited.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => { setShowBookedConfirm(false); saveMutation.mutate(); }}
                data-testid={`button-confirm-booked-${artistSlug}`}
                style={{ flex: 1, background: "#ef4444", border: "none", borderRadius: 8, padding: "11px 18px", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
              >
                Yes, Set Booked Out
              </button>
              <button
                onClick={() => setShowBookedConfirm(false)}
                data-testid={`button-cancel-booked-${artistSlug}`}
                style={{ flex: 1, background: "#ffffff10", border: "1px solid #ffffff20", borderRadius: 8, padding: "11px 18px", color: "#ccc", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <button
          onClick={() => {
            if (status === "booked" && data.status !== "booked") {
              setShowBookedConfirm(true);
            } else {
              saveMutation.mutate();
            }
          }}
          disabled={saveMutation.isPending}
          data-testid={`button-save-${artistSlug}`}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "10px 20px", background: saved ? "#22c55e" : effectiveAccent,
            color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: saveMutation.isPending ? "not-allowed" : "pointer",
            opacity: saveMutation.isPending ? 0.7 : 1, minHeight: 44, transition: "background 0.2s",
          }}
        >
          {saveMutation.isPending ? (
            <RefreshCw style={{ width: 15, height: 15, animation: "spin 0.8s linear infinite" }} />
          ) : saved ? (
            <Check style={{ width: 15, height: 15 }} />
          ) : (
            <Save style={{ width: 15, height: 15 }} />
          )}
          {saveMutation.isPending ? "Saving…" : saved ? "Saved!" : "Save Changes"}
        </button>

        <button
          onClick={() => setShowCalendar((v) => !v)}
          data-testid={`button-toggle-calendar-${artistSlug}`}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "10px 18px",
            background: showCalendar ? "#ef444418" : "#ffffff08",
            color: showCalendar ? "#ef4444" : "#aaa",
            border: `1px solid ${showCalendar ? "#ef444430" : "#ffffff15"}`,
            borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", minHeight: 44,
          }}
        >
          <CalendarOff style={{ width: 15, height: 15 }} />
          Block Days
          {localBlockedDates.length > 0 && (
            <span style={{ background: "#ef444430", color: "#ef4444", fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 10, marginLeft: 2 }}>
              {localBlockedDates.length}
            </span>
          )}
        </button>

        {saveMutation.isError && (
          <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#ef4444", fontSize: 12 }}>
            <AlertCircle style={{ width: 14, height: 14 }} />
            {(saveMutation.error as Error).message}
          </span>
        )}
      </div>

      {/* Blocked dates calendar */}
      <AnimatePresence initial={false}>
        {showCalendar && (
          <motion.div
            key="calendar"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{ borderTop: "1px solid #ffffff08", marginTop: 16 }}
          >
            <BlockedDaysCalendar
              artistSlug={artistSlug}
              blockedDates={localBlockedDates}
              recurringBlockedDays={localRecurringDays}
              accentColor={effectiveAccent}
              blockedDatesUrl={blockedDatesUrl}
              recurringDaysUrl={recurringDaysUrl}
              queryKey={queryKey}
              onBlockedDatesChange={setLocalBlockedDates}
              onRecurringDaysChange={setLocalRecurringDays}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
